import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { 
  CallStatusUpdate, 
  BatchStatusUpdate, 
  CallErrorUpdate 
} from '../types/api';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  onCallStatus: (callback: (data: CallStatusUpdate) => void) => () => void;
  onBatchStatus: (callback: (data: BatchStatusUpdate) => void) => () => void;
  onCallError: (callback: (data: CallErrorUpdate) => void) => () => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const { user } = useAuth();
  
  // Use ref to track if socket is being initialized
  const socketInitializing = useRef(false);
  const userId = user?.id; // Extract just the ID to prevent unnecessary re-renders

  useEffect(() => {
    // Don't create socket if no user
    if (!userId) {
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    // Prevent duplicate socket creation
    if (socketInitializing.current || socket?.connected) {
      return;
    }

    socketInitializing.current = true;

    const socketUrl = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'http://localhost:5000';
    
    const newSocket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity, // Keep trying to reconnect
      timeout: 20000,
    });

    newSocket.on('connect', () => {
      setIsConnected(true);
      
      const token = localStorage.getItem('token');
      if (token) {
        newSocket.emit('authenticate', { token });
      } else {
        console.error('❌ No token available for socket authentication');
      }
    });

    newSocket.on('authenticated', (data) => {
    });

    newSocket.on('authentication-failed', (data) => {
      console.error('❌ Socket authentication failed:', data);
    });

    newSocket.on('disconnect', (reason) => {
      setIsConnected(false);
      
      // Don't try to reconnect if it was a manual disconnect
      if (reason === 'io client disconnect') {
        socketInitializing.current = false;
      }
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      setIsConnected(false);
    });

    newSocket.on('reconnect', (attemptNumber) => {
      setIsConnected(true);
      
      // Re-authenticate after reconnection
      const token = localStorage.getItem('token');
      if (token) {
        newSocket.emit('authenticate', { token });
      }
    });

    newSocket.on('reconnect_attempt', (attemptNumber) => {
    });

    newSocket.on('reconnect_error', (error) => {
      console.error('Socket reconnection error:', error);
    });

    newSocket.on('reconnect_failed', () => {
      console.error('❌ Socket reconnection failed after all attempts');
    });

    setSocket(newSocket);
    socketInitializing.current = false;

    // Cleanup function
    return () => {
      if (newSocket) {
        newSocket.removeAllListeners();
        newSocket.disconnect();
      }
        socketInitializing.current = false;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

  const onCallStatus = useCallback((callback: (data: CallStatusUpdate) => void) => {
    if (!socket) {
      console.warn('Socket not available for call-status listener');
      return () => {};
    }

    socket.on('call-status', callback);

    return () => {
      socket.off('call-status', callback);
    };
  }, [socket]);

  const onBatchStatus = useCallback((callback: (data: BatchStatusUpdate) => void) => {
    if (!socket) {
      console.warn('Socket not available for batch-status listener');
      return () => {};
    }

    socket.on('batch-status', callback);

    return () => {
      socket.off('batch-status', callback);
    };
  }, [socket]);

  const onCallError = useCallback((callback: (data: CallErrorUpdate) => void) => {
    if (!socket) {
      console.warn('Socket not available for call-error listener');
      return () => {};
    }

    socket.on('call-error', callback);

    return () => {
      socket.off('call-error', callback);
    };
  }, [socket]);

  return (
    <SocketContext.Provider
      value={{
        socket,
        isConnected,
        onCallStatus,
        onBatchStatus,
        onCallError,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};